/**
 * Gtag plugin: same event names and API as main project (frontendrealestate).
 * Uses generated event names from analytics/events.generated.ts.
 * If the host page (e.g. WordPress) already has gtag/dataLayer, events are pushed there.
 * Optional: pass gtagId in widget config to inject the GA script when not present.
 */
import { GA_EVENT, GA_CATEGORY, GTM_EVENT } from '../analytics/events.generated'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

function ensureDataLayer() {
  if (typeof window === 'undefined') return false
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args)
    }
  }
  return true
}

/**
 * Initialize gtag script (optional). Call when widget is embedded without host GA.
 * Idempotent; safe to call with same measurementId multiple times.
 */
export function initGtag(measurementId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  ensureDataLayer()
  window.gtag('js', new Date())
  if (!document.getElementById('ga-gtag')) {
    const script = document.createElement('script')
    script.id = 'ga-gtag'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    document.head.appendChild(script)
  }
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    allow_google_signals: false,
    send_page_view: true,
  })
}

/**
 * Track a generic GA event. Same signature as main project.
 */
export function trackEvent(action: string, category: string, label?: string, value?: number): void {
  if (!ensureDataLayer()) return
  try {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    })
  } catch {
    // Analytics must never break UX
  }
}

export type LeadFormTrackingContext = {
  propertyId?: string
}

export type LeadFormConversionContext = {
  conversationId?: string
  propertyId?: string
}

/**
 * Track lead form step submit (step 1 or 2). Same as main project.
 */
export function trackLeadFormStepSubmit(step: 1 | 2, context?: LeadFormTrackingContext): void {
  const labelParts: string[] = []
  if (context?.propertyId) {
    labelParts.push(`property:${context.propertyId}`)
  }
  const label = labelParts.length ? labelParts.join('|') : undefined
  const action = step === 1 ? GA_EVENT.LEAD_FORM_STEP_1_SUBMIT : GA_EVENT.LEAD_FORM_STEP_2_SUBMIT
  trackEvent(action, GA_CATEGORY.LEAD_FORM, label)
}

/**
 * Track lead form conversion: push to dataLayer for GTM (e.g. Google Ads) and send GA event.
 * Same as main project.
 */
export function trackLeadFormConversion(context: LeadFormConversionContext): void {
  if (!ensureDataLayer()) return
  try {
    const eventPayload: Record<string, unknown> = {
      event: GTM_EVENT.LEAD_FORM_SUBMIT,
    }
    if (context.conversationId) {
      eventPayload.conversation_id = context.conversationId
    }
    if (context.propertyId) {
      eventPayload.property_id = context.propertyId
    }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(eventPayload)
    }
    if (context.conversationId) {
      trackEvent(GA_EVENT.LEAD_FORM_CONVERSION, GA_CATEGORY.LEAD_FORM, context.conversationId)
    } else {
      trackEvent(GA_EVENT.LEAD_FORM_CONVERSION, GA_CATEGORY.LEAD_FORM)
    }
  } catch {
    // Analytics must never break UX
  }
}

/**
 * Lead open — contact modal opened. Same as main project (lead_open / contact).
 */
export function trackLeadOpen(propertyIdOrShortRef?: string): void {
  trackEvent(GA_EVENT.LEAD_OPEN, GA_CATEGORY.CONTACT, propertyIdOrShortRef)
}

/**
 * Lead submit attempt (user clicked send). Same as main project.
 */
export function trackLeadSubmitAttempt(propertyId?: string): void {
  trackEvent(GA_EVENT.LEAD_SUBMIT_ATTEMPT, GA_CATEGORY.CONTACT, propertyId)
}

/**
 * Lead submit success. Same as main project.
 */
export function trackLeadSubmitSuccess(propertyId?: string): void {
  trackEvent(GA_EVENT.LEAD_SUBMIT_SUCCESS, GA_CATEGORY.CONTACT, propertyId)
}

/**
 * Lead submit error. Same as main project.
 */
export function trackLeadSubmitError(context?: { propertyId?: string; code?: string }): void {
  const parts = [
    context?.propertyId ? `prop=${context.propertyId}` : null,
    context?.code ? `code=${context.code}` : null,
  ].filter(Boolean)
  const label = parts.length ? parts.join(';') : undefined
  trackEvent(GA_EVENT.LEAD_SUBMIT_ERROR, GA_CATEGORY.CONTACT, label)
}
